import {
  Arrow as TooltipPrimitiveArrow,
  Content as TooltipPrimitiveContent,
  Portal as TooltipPrimitivePortal,
  Provider as TooltipPrimitiveProvider,
  Root as TooltipPrimitiveRoot,
  Trigger as TooltipPrimitiveTrigger,
} from "@radix-ui/react-tooltip";

import React from "react";

import { BodyText } from "@/components/ui/Text/BodyText";
import { cn } from "@/lib/utils";

interface TooltipProps {
  tooltipContentText?: string;
  tooltipContentComponent?: React.ReactNode;
  side?: "top" | "right" | "bottom" | "left";
  showArrow?: boolean;
  triggerClassName?: string;
  contentClassName?: string;
}

const Tooltip = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitiveContent>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitiveRoot> & TooltipProps
>(
  (
    {
      tooltipContentText,
      tooltipContentComponent,
      side = "top",
      showArrow = false,
      children,
      triggerClassName,
      contentClassName,
    },
    ref,
  ) => {
    if (!tooltipContentText && !tooltipContentComponent) {
      return children;
    }

    return (
      <TooltipPrimitiveProvider>
        <TooltipPrimitiveRoot delayDuration={0}>
          <TooltipPrimitiveTrigger asChild>
            <span className={triggerClassName}>{children}</span>
          </TooltipPrimitiveTrigger>
          <TooltipPrimitivePortal>
            <TooltipPrimitiveContent
              className={cn(
                "z-50 bg-inverse px-2.5 py-1 rounded-md",
                contentClassName,
              )}
              side={side}
              sideOffset={4}
              ref={ref}
            >
              {tooltipContentComponent || (
                <BodyText className="text-white" size="sm">
                  {tooltipContentText}
                </BodyText>
              )}
              {showArrow && <TooltipPrimitiveArrow />}
            </TooltipPrimitiveContent>
          </TooltipPrimitivePortal>
        </TooltipPrimitiveRoot>
      </TooltipPrimitiveProvider>
    );
  },
);

Tooltip.displayName = TooltipPrimitiveRoot.displayName;

export {
  Tooltip,
  TooltipPrimitiveRoot as TooltipRoot,
  TooltipPrimitiveTrigger as TooltipTrigger,
  TooltipPrimitiveContent as TooltipContent,
  TooltipPrimitiveArrow as TooltipArrow,
  TooltipPrimitiveProvider as TooltipProvider,
};
